import React, { useState, useEffect } from 'react';
import Papa from 'papaparse';
import QRCode from 'qrcode';
import './Settings.css';
import { useAppContext } from '../context/AppContext';
import {
  getCurrentUser,
  setUpTOTP,
  verifyTOTPSetup,
  fetchMFAPreference,
  signOut,
  fetchAuthSession,
} from '@aws-amplify/auth';
import {
  CognitoIdentityProviderClient,
  SetUserMFAPreferenceCommand,
} from '@aws-sdk/client-cognito-identity-provider';

const Settings = () => {
  const { importCases, mfaEnabled, setMfaEnabled } = useAppContext();

  const [region, setRegion] = useState(localStorage.getItem('region') || '');
  const [importStatus, setImportStatus] = useState(null);
  const [qrCode, setQrCode] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [code, setCode] = useState('');
  const [statusMsg, setStatusMsg] = useState('');

  useEffect(() => {
    checkMfaStatus();
  }, []);

  const checkMfaStatus = async () => {
    try {
      const pref = await fetchMFAPreference();
      setMfaEnabled(pref?.preferred === 'TOTP');
    } catch (err) {
      console.error('❌ Error checking MFA status:', err);
    }
  };

  const enableMFA = async () => {
    try {
      if (secretKey) return;
      const user = await getCurrentUser();
      const { sharedSecret } = await setUpTOTP();

      const issuer = encodeURIComponent('TrafficApp');
      const username = encodeURIComponent(user.username);
      const otpAuthUrl = `otpauth://totp/${issuer}:${username}?secret=${sharedSecret}&issuer=${issuer}&algorithm=SHA1&digits=6&period=30`;
      const qrDataUrl = await QRCode.toDataURL(otpAuthUrl);

      setQrCode(qrDataUrl);
      setSecretKey(sharedSecret);
      setStatusMsg('');
    } catch (error) {
      console.error('❌ Failed to enable MFA:', error);
      setStatusMsg('❌ Failed to initialize MFA.');
    }
  };

  const verifyAndEnableMFA = async () => {
    try {
      const cleanCode = code.trim();
      if (!cleanCode || cleanCode.length !== 6) {
        setStatusMsg('❌ Please enter a valid 6-digit code.');
        return;
      }

      await verifyTOTPSetup({ code: cleanCode });

      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();

      const client = new CognitoIdentityProviderClient({ region: 'us-east-2' });
      const command = new SetUserMFAPreferenceCommand({
        AccessToken: token,
        SoftwareTokenMfaSettings: {
          Enabled: true,
          PreferredMfa: true,
        },
      });

      await client.send(command);

      setStatusMsg('✅ 2FA enabled successfully');
      setMfaEnabled(true);
      setQrCode('');
      setSecretKey('');
      setCode('');
    } catch (error) {
      console.error('❌ Failed to confirm TOTP:', error);
      setStatusMsg('❌ Failed to enable 2FA. Check the code and try again.');
    }
  };

  const disableMFA = async () => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();

      const client = new CognitoIdentityProviderClient({ region: 'us-east-2' });
      const command = new SetUserMFAPreferenceCommand({
        AccessToken: token,
        SoftwareTokenMfaSettings: {
          Enabled: false,
          PreferredMfa: false,
        },
      });

      await client.send(command);
      setStatusMsg('✅ 2FA disabled');
      setMfaEnabled(false);
      setQrCode('');
      setSecretKey('');
      setCode('');
    } catch (error) {
      console.error('❌ Failed to disable 2FA:', error);
      setStatusMsg('❌ Failed to disable 2FA.');
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        const imported = results.data.map((row) => {
          const normalizeBalance = (val) => {
            const clean = (val || '').trim().toLowerCase();
            if (!clean) return 'NOT PAID';
            if (['n', 'no'].includes(clean)) return 'PAID IN FULL';
            if (['y', 'yes'].includes(clean)) return 'NOT PAID';
            return val;
          };

          return {
            ...row,
            id: row.id || crypto.randomUUID(),
            courtDateTime: row.courtDateTime || row.courtDate || '',
            balanceDue: normalizeBalance(row.balanceDue),
            status: row.status || 'Open',
          };
        });

        try {
          await importCases(imported);
          setImportStatus(`✅ Imported ${imported.length} cases`);
        } catch (err) {
          console.error('❌ Bulk import failed:', err);
          setImportStatus(`❌ Import failed. See console for details.`);
        }

        setTimeout(() => setImportStatus(null), 4000);
      },
    });
  };

  const downloadTemplate = () => {
    const headers = [
      'id',
      'firstName',
      'lastName',
      'dob',
      'licenseNumber',
      'phone',
      'secondaryPhone',
      'tertiaryPhone',
      'email',
      'mailingAddress',
      'incidentCounty',
      'caseType',
      'officerName', // ✅ newly added
      'appearanceNumber', // ✅ Ticket #
      'idnumber',
      'courtDateTime',
      'disposition',
      'totalFee',
      'paidAmount',
      'amountDue',
      'balanceDue',
      'status',
      'comments',
    ];

    const csv = `${headers.join(',')}\n`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'case_import_template.csv';
    link.click();
  };

  return (
    <div className="container settings-page">
      {!mfaEnabled && (
        <div className="mfa-banner">
          ⚠️ You must enable Two-Factor Authentication (2FA) to use the full application.
        </div>
      )}

      <h2>Settings</h2>

      {mfaEnabled && (
        <>
          <div className="settings-section">
            <label>Import Cases via CSV</label>
            <div className="file-upload-wrapper">
              <label className="file-upload-label small">
                📎 Upload File
                <input type="file" accept=".csv" onChange={handleFileUpload} />
              </label>
            </div>
            {importStatus && <p className="import-status">{importStatus}</p>}
            <button className="download-template" onClick={downloadTemplate}>
              📄 Download CSV Template
            </button>
          </div>
        </>
      )}

      <div className="settings-section">
        <label>Two-Factor Authentication (2FA)</label>
        {mfaEnabled ? (
          <>
            <p>✅ 2FA is enabled</p>
            <button onClick={disableMFA}>❌ Disable 2FA</button>
          </>
        ) : (
          <>
            <p>❌ 2FA is not enabled</p>
            <button onClick={enableMFA} disabled={!!secretKey}>
              🔒 Enable 2FA
            </button>
          </>
        )}

        {qrCode && (
          <>
            <p>Scan this QR code in your authenticator app:</p>
            <img src={qrCode} alt="QR Code" width="200" />
            <p>Or manually enter this setup key:</p>
            <code>{secretKey}</code>
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              placeholder="Enter 6-digit code"
              style={{ display: 'block', marginTop: '1rem', padding: '0.5rem' }}
            />
            <button onClick={verifyAndEnableMFA} style={{ marginTop: '0.5rem' }}>
              ✅ Confirm 2FA
            </button>
          </>
        )}

        {statusMsg && <p className="import-status">{statusMsg}</p>}
      </div>

      <button
        onClick={async () => {
          await signOut();
          window.location.reload();
        }}
        style={{
          marginTop: '2rem',
          backgroundColor: '#ef4444',
          color: 'white',
          padding: '0.6rem 1.2rem',
          borderRadius: '6px',
          fontSize: '1rem',
          cursor: 'pointer',
        }}
      >
        🔓 Sign Out
      </button>

      <footer className="settings-footer">
        © {new Date().getFullYear()} Zbrella Inc. All rights reserved.
      </footer>
    </div>
  );
};

export default Settings;
